import React from 'react';
import Icon from '../../../components/AppIcon';

const PermissionSummary = ({ permissions, selectedRole }) => {
  const calculateSummary = () => {
    if (!permissions || permissions?.length === 0) {
      return {
        totalCategories: 0,
        activeCategories: 0,
        totalPermissions: 0,
        activePermissions: 0
      };
    }

    const totalCategories = permissions?.length;
    const activeCategories = permissions?.filter(p => 
      p?.canView || p?.canEdit || p?.canCreate || p?.canDelete || p?.canExport
    )?.length;

    const totalPermissions = totalCategories * 5;
    const activePermissions = permissions?.reduce((count, perm) => {
      return count + [
        perm?.canView,
        perm?.canEdit,
        perm?.canCreate,
        perm?.canDelete,
        perm?.canExport
      ]?.filter(Boolean)?.length;
    }, 0);

    return {
      totalCategories,
      activeCategories,
      totalPermissions,
      activePermissions
    };
  };

  const summary = calculateSummary();
  const permissionPercentage = summary?.totalPermissions > 0 
    ? Math.round((summary?.activePermissions / summary?.totalPermissions) * 100)
    : 0;

  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Full system access with all permissions enabled';
      case 'analyst':
        return 'Read-only access with export capabilities';
      case 'merchant':
        return 'Limited access to merchant-specific features';
      default:
        return 'No role selected';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Permission Summary</h3>
        <Icon name="Info" size={20} className="text-muted-foreground" />
      </div>

      {selectedRole ? (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Overall Access</span>
              <span className="text-sm font-semibold text-foreground">{permissionPercentage}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-3">
              <div
                className="bg-primary h-3 rounded-full transition-all"
                style={{ width: `${permissionPercentage}%` }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 text-primary p-2 rounded-lg">
                  <Icon name="Layers" size={20} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Feature Categories</p>
                  <p className="text-xs text-muted-foreground">Active categories</p>
                </div>
              </div>
              <span className="text-lg font-bold text-foreground">
                {summary?.activeCategories}/{summary?.totalCategories}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="bg-success/10 text-success p-2 rounded-lg">
                  <Icon name="CheckCircle2" size={20} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Total Permissions</p>
                  <p className="text-xs text-muted-foreground">Enabled permissions</p>
                </div>
              </div>
              <span className="text-lg font-bold text-foreground">
                {summary?.activePermissions}/{summary?.totalPermissions}
              </span>
            </div>
          </div>

          <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-foreground">
              <span className="font-semibold capitalize">{selectedRole}:</span>{' '}
              {getRoleDescription(selectedRole)}
            </p>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Shield" size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-sm">Select a role to view permission summary</p>
        </div>
      )}
    </div>
  );
};

export default PermissionSummary;